import Slider from 'react-slick'
import { useEffect } from 'react'
import { useRef, useState } from 'react'
import { TeamMember } from './TeamMember'
import { FaCaretRight, FaCaretLeft } from 'react-icons/fa6'
import clsx from 'clsx'

interface TeamMember {
  id: string
  name: string
  avatarUrl: string
  avatarBg: string
  avatarStrokeColor: string
  description: string
  shadow: string
}

interface TeamSliderProps {
  members: TeamMember[]
  activeId: string
  onChangeActiveId: (id: string) => void
}

export function TeamSlider({
  members,
  activeId,
  onChangeActiveId,
}: TeamSliderProps) {
  const sliderRef = useRef<any>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesToShow, setSlidesToShow] = useState(5)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  )

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isMobile = windowWidth <= 768

  const settings = {
    dots: false,
    speed: 500,
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    slidesToShow: isMobile ? 3 : 5,
    slidesToScroll: 1,
    arrows: false,
    infinite: isMobile,
    autoplay: isMobile,
    autoplaySpeed: 3000,
    afterChange: (index: number) => {
      setCurrentSlide(index)
      setSlidesToShow(isMobile ? 3 : 5)
    },
  }

  const totalSlides = members.length
  const atStart = currentSlide === 0
  const atEnd = currentSlide >= totalSlides - slidesToShow

  return (
    <div className='mx-6 lg:mx-0 lg:ml-6 lg:mt-6'>
      <p className='w-full py-4 text-center lg:text-left text-sm text-[#001A3D] font-medium leading-[16px] tracking-wide'>
        Our team
      </p>
      <Slider
        key={windowWidth}
        ref={sliderRef}
        {...settings}
        className='overflow-hidden w-full inline-flex items-center py-2 rounded-[20px] border border-[#D9D9D9]/50 shadow-[0px_1px_10px_0px_rgba(0,0,0,0.10)] lg:flex lg:rounded-[12px] lg:bg-[#FFFFFF] bg-white/10'
      >
        {members.map((member) => (
          <div className='p-2'>
            <TeamMember
              key={member.id}
              name={member.name}
              avatarUrl={member.avatarUrl}
              avatarBg={member.avatarBg}
              avatarStrokeColor={member.avatarStrokeColor}
              shadow={member.shadow}
              isActive={activeId === member.name}
              onClick={() => onChangeActiveId(member.name)}
            />
          </div>
        ))}
      </Slider>
      <div className='hidden lg:flex items-start gap-[20px] lg:gap-[35px] justify-end w-full mt-3'>
        <FaCaretLeft
          className={clsx(
            'text-lg lg:text-3xl cursor-pointer',
            atStart && 'opacity-30 cursor-not-allowed',
          )}
          onClick={() => !atStart && sliderRef.current?.slickPrev()}
        />
        <FaCaretRight
          className={clsx(
            'text-lg lg:text-3xl cursor-pointer',
            atEnd && 'opacity-30 cursor-not-allowed',
          )}
          onClick={() => !atEnd && sliderRef.current?.slickNext()}
        />
      </div>
    </div>
  )
}
